<script setup lang="ts">
import { SELECTABLE_ITEMS } from '@/services/consts/daily_report';
import { ref, Ref, computed, onMounted, onUnmounted } from 'vue';
import DeliveryPlanType from '@/types/DeliveryPlan';

const props = defineProps<{
  deliveryPlan: DeliveryPlanType
  isOperationOffice: boolean
  isDepartment: boolean
}>()

const BUTTON_ID = `button-${props.deliveryPlan.delivery_plan_id}`;
const PULL_DOWN_ID = `drop-down-${props.deliveryPlan.delivery_plan_id}`;
const isOpenPullDown: Ref<boolean> = ref(false);

interface EditableAction {
  available: boolean;
  processing?: boolean;
}

interface EditableActions {
  alterLeaving?: EditableAction;
  alterReturning?: EditableAction;
  divide?: EditableAction;
  merge?: EditableAction;
  [key: string]: EditableAction | undefined;
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

const displayPullDownItems = computed<{[key: string]: string}[]>(() => {
  const items: {[key: string]: string}[] = [];
  const editableActions: EditableActions = props.deliveryPlan?.editable_actions || {};
  Object.entries(editableActions).forEach(([key, action]) => {
    if (action && action.available) {
      switch (key) {
        case 'alter_leaving':
          if (!props.isOperationOffice && !props.isDepartment) {
            items.push(SELECTABLE_ITEMS['alterLeaving']);
          }
          break;
        case 'alter_returning':
          if (!props.isOperationOffice && !props.isDepartment) {
            items.push(SELECTABLE_ITEMS['alterReturning']);
          }
          break;
        case 'divide':
          items.push(SELECTABLE_ITEMS['divide']);
          break;
        case 'merge':
          items.push(SELECTABLE_ITEMS['merge']);
          break;
        case 'recreate':
          if (!props.isOperationOffice && !props.isDepartment) {
            items.push(SELECTABLE_ITEMS['recreate']);
          }
          break;
        case 'combine':
          if (!props.isOperationOffice && !props.isDepartment && props.deliveryPlan.driver) {
            items.push(SELECTABLE_ITEMS['combine']);
          }
          break;
        case 'combine_rollback':
          if (!props.isOperationOffice && !props.isDepartment) {
            items.push(SELECTABLE_ITEMS['combine_rollback']);
          }
          break;
      }
    }
  });

  return items;
});

function handleClickOutside(event: MouseEvent) {
  const target = event.target as Node;
  const targetId = (target as HTMLElement).id;
  if (targetId == BUTTON_ID) {
    return isOpenPullDown.value = true;
  }
  if (targetId != PULL_DOWN_ID) {
    return isOpenPullDown.value = false;
  }
}

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div
    v-if="displayPullDownItems.length"
    class="position-relative"
  >
    <button
      :id="BUTTON_ID"
      class="d-flex material-symbols-outlined text-black text-decoration-none"
      @click="handleClickOutside($event)"
    >
      more_vert
    </button>
    <div
      v-if="isOpenPullDown"
      ref="pullDownRef"
      :id="PULL_DOWN_ID"
      class="pull-down position-absolute top-0 h-100 z-3"
      @click.stop
    >
      <template
        v-for="item in displayPullDownItems"
        :key="Object.keys(item)[0]"
      >
        <router-link
          :to="{ name: Object.keys(item)[0], params: { id: props.deliveryPlan.delivery_plan_id }}"
          class="d-flex text-black text-decoration-none"
        >
          <div
            class="candidate-list p-2 d-flex text-nowrap w-100 justify-content-center z-3"
          >
            {{ Object.values(item)[0] }}
          </div>
        </router-link>
      </template>
    </div>
  </div>
</template>

<style scoped>
.pull-down {
  left: 15%;
  width: 180px;
}
.candidate-list {
  cursor: pointer;
  font-size: 90%;
  background: #ffffff;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.2);
  z-index: 3;
}
.candidate-list:hover {
  background: #f8f9fa;
}
</style>
